import { combineReducers } from 'redux';
import { mergeSchedules, splitInterval } from '../../../utils/schedules';
import * as types from './types';
import { uniqWith, isEqual } from 'lodash';

const initialState = {
  loading: false,
  membership: {},
  group: {},
  team: {},
  task: {},
  scheduleWarnings: {}
};

// Received a bulk update, which contains a list of normal
// COMBINED_SCHEDULE_UPDATED actions. Apply these one by one, but only the
// unique ones
const batchUpdate = (state, action) =>
  uniqWith(action.payload.actions, isEqual).reduce((currentState, p) => fullScheduleReducer(currentState, p), state);

export const fullScheduleReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.COMBINED_SCHEDULE_UPDATED: {
      const ownerType = action.payload.ownerType;
      // ignore updates about entity types we don't care about, such as 'station'
      if (!state[ownerType]) {
        return state;
      }

      const oldSchedule = state[ownerType][action.payload.id];
      const newSchedule = action.payload.schedule;
      const mergeMode = action.mergeMode;
      const overwriteUnsavedChanges = action.overwriteUnsavedChanges;

      // if there are any unsaved changes in this entity's schedule, don't do
      // any updates. This is to avoid situations in e.g. the duty planner
      // where:
      // 1. the user makes a change and saves it
      // 2. the user makes more changes
      // 3. the websocket update from #1 comes in and overwrites #2
      const intervals = oldSchedule?.intervals || [];
      if (!overwriteUnsavedChanges && intervals.some(i => i.transient)) return state;

      return {
        ...state,
        loading: false,
        [ownerType]: {
          ...state[ownerType],
          [action.payload.id]: mergeSchedules(oldSchedule, newSchedule, mergeMode)
        }
      };
    }
    case types.BATCH_COMBINED_SCHEDULE_UPDATED: {
      return batchUpdate(state, action);
    }
    case types.SPLIT_COMBINED_SCHEDULE_INTERVAL: {
      const { ownerType, ownerId, time } = action.payload;
      const schedule = state[ownerType][ownerId];
      return {
        ...state,
        [ownerType]: {
          ...state[ownerType],
          [ownerId]: splitInterval(schedule, time)
        }
      };
    }
    case types.ERROR: {
      // we still want to leave existing data intact as well as "last fetch"
      // which would let us determine if the data is stale or not
      const errorTimes = [...(state.errorTimes || []), action.timestamp];
      return {
        ...state,
        error: action.error,
        errorTimes,
        loading: false
      };
    }
    case types.SCHEDULE_WARNINGS_RECEIVED: {
      let newState = { ...state };

      const { scheduleWarnings } = action.payload;
      const warningTypes = Object.keys(scheduleWarnings);

      for (const warningType of warningTypes) {
        const existingWarnings = state.scheduleWarnings[warningType] || [];
        const newWarnings = scheduleWarnings[warningType];

        newState = {
          ...state,
          scheduleWarnings: {
            ...state.scheduleWarnings,
            [warningType]: [...existingWarnings, ...newWarnings]
          }
        };
      }

      return newState;
    }
    case types.SCHEDULE_WARNINGS_CLEARED: {
      return { ...state, scheduleWarnings: {} };
    }
    case types.START: {
      return { ...state, loading: true };
    }
    case types.SUCCESS: {
      return { ...state, loading: false, error: null, errorTimes: null };
    }
    default: {
      return state;
    }
  }
};

export default combineReducers({
  full: fullScheduleReducer
});
